import { sectionRenderer } from '@theme/section-renderer';

/**
 * The blog landing.
 *
 * Two jobs, both upgrades on markup that already works without them:
 *
 *   1. Tabs. Each is an ordinary `<a href="/blogs/handle">`, so with this
 *      module missing (or blocked) a tab is a normal page load onto the same
 *      template. Here a click re-requests THIS section for that blog through
 *      the Section Rendering API and swaps the body in, so the header, the
 *      drawer and the ticker never repaint.
 *
 *   2. Load more. Every article is already in the grid — Liquid cannot
 *      paginate the merged index, and rendering the lot keeps the whole blog
 *      crawlable on one URL. This hides everything past the first batch and
 *      hands it back a batch at a time. The control itself ships hidden, so
 *      with no JavaScript there is no dead button, just the full list.
 *
 * The swap replaces `innerHTML`, never the element, so these listeners (bound
 * on the host and delegated) survive every exchange.
 */
class ItBlog extends HTMLElement {
  #reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  #busy = false;
  #shown = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPop);
    this.#paginate(true);
    this.#syncTabScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPop);
  }

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #tiles() {
    return [...(this.querySelector('[data-grid]')?.children ?? [])];
  }

  #onClick = (event) => {
    const tab = event.target.closest('.it-blog__tab');
    if (tab && this.contains(tab)) {
      if (tab.classList.contains('is-on')) {
        event.preventDefault();
        return;
      }
      if (!this.#plainClick(event)) return;
      event.preventDefault();
      this.#goto(tab.href, true);
      return;
    }

    if (event.target.closest('[data-next]')) this.#more();
  };

  /** A modified click is the visitor asking for a new tab — leave it alone. */
  #plainClick(event) {
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);
  }

  #onPop = () => {
    this.#goto(location.href, false);
  };

  /* ---------- reveal ---------- */

  /**
   * Hide everything past the batch and light up the control.
   * @param {boolean} reset - Start again from the first batch
   */
  #paginate(reset) {
    const wrap = this.querySelector('[data-more]');
    const count = this.querySelector('[data-count]');
    const tiles = this.#tiles;
    const size = Number(count?.dataset.page) || 12;

    if (reset) this.#shown = Math.min(size, tiles.length);

    tiles.forEach((tile, index) => {
      tile.hidden = index >= this.#shown;
    });

    if (!wrap) return;
    wrap.hidden = tiles.length === 0;
    wrap.querySelector('[data-next]')?.toggleAttribute('hidden', this.#shown >= tiles.length);
    if (count) count.textContent = `${Math.min(this.#shown, tiles.length)} of ${tiles.length} articles`;
  }

  #more() {
    const tiles = this.#tiles;
    const size = Number(this.querySelector('[data-count]')?.dataset.page) || 12;
    const from = this.#shown;

    this.#shown = Math.min(this.#shown + size, tiles.length);
    this.#paginate(false);
    this.#enter(tiles.slice(from, this.#shown));

    // A keyboard visitor pressed a button that may have just retired — hand
    // them the first tile they asked for rather than the top of the document.
    const first = tiles[from];
    if (first && this.querySelector('[data-next]')?.hidden) {
      first.setAttribute('tabindex', '-1');
      first.focus({ preventScroll: true });
    }
  }

  /* ---------- tabs ---------- */

  /**
   * Swap the whole section body to another blog's.
   * @param {string} url - The blog URL to render
   * @param {boolean} push - Whether to write the URL into history
   */
  async #goto(url, push) {
    if (this.#busy) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    let fresh = null;
    try {
      const html = await sectionRenderer.getSectionHTML(this.#sectionId, false, new URL(url, location.origin));
      fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('it-blog');
    } catch {
      /* offline, aborted, section renamed — fall through to a real navigation */
    }

    this.#busy = false;
    this.removeAttribute('aria-busy');

    // Nothing came back: navigate for real rather than leave the visitor on a
    // tab that did not change.
    if (!fresh) {
      location.href = url;
      return;
    }

    if (push) history.pushState({ itBlog: true }, '', url);

    this.className = fresh.className;
    this.dataset.allHandle = fresh.dataset.allHandle ?? this.dataset.allHandle;
    this.innerHTML = fresh.innerHTML;

    this.#paginate(true);
    this.#enter(this.#tiles.filter((tile) => !tile.hidden));
    this.#syncTabScroll();
  }

  /**
   * The staggered rise. Driven by a forced reflow rather than
   * requestAnimationFrame, which does not fire in a background tab: a tile
   * whose entrance never completes would be a tile that never shows.
   * @param {Element[]} tiles - Tiles to bring in
   */
  #enter(tiles) {
    if (this.#reduce) return;
    tiles.forEach((tile, index) => {
      tile.classList.add('is-pre');
      tile.style.transitionDelay = `${index * 45}ms`;
      void tile.offsetHeight;
      tile.classList.remove('is-pre');
    });
  }

  /** Keep the chosen tab in view on the phone's swiped rail. */
  #syncTabScroll() {
    if (window.innerWidth >= 920) return;
    this.querySelector('.it-blog__tab.is-on')?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: this.#reduce ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('it-blog')) {
  customElements.define('it-blog', ItBlog);
}
